import {use, useCallback, useMemo} from 'react'

import {RoadmapDateFieldsPopoverDismissed, RoadmapDateFieldsUIPopover} from '../../api/stats/contracts'
import {getDateFieldToolbarStatsContext} from '../../api/stats/helpers'
import {ViewTypeParam} from '../../api/view/contracts'
import {isRoadmapColumnModel, type ReadonlyRoadmapColumns} from '../../helpers/roadmap-helpers'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {RoadmapSessionSettingsContext, RoadmapSettingsContext} from '../../hooks/use-roadmap-settings'
import {useViews} from '../../hooks/use-views'
import {RoadmapDateFieldsMenuContext} from './roadmap-date-fields-menu'

type InitialFieldType = 'iteration' | 'date'

type RoadmapDateFieldsPopoverState = {
  showDateFieldsPopover: boolean
  showInitialSelectedFieldsMessage: boolean
  initialFieldType: InitialFieldType
  handleDateFieldsPopoverPrimaryAction: () => void
}

const EMPTY_DATE_FIELDS: ReadonlyRoadmapColumns = []

export const useRoadmapDateFieldsPopover = (): RoadmapDateFieldsPopoverState => {
  const roadmapSettings = use(RoadmapSettingsContext)
  const roadmapSessionSettings = use(RoadmapSessionSettingsContext)
  const roadmapDateFieldsMenu = use(RoadmapDateFieldsMenuContext)
  const {currentView} = useViews()
  const {postStats} = usePostStats()

  const dateFields = roadmapSettings?.dateFields ?? EMPTY_DATE_FIELDS
  const areDateFieldsDefault = roadmapSettings?.areDateFieldsDefault ?? false
  const isDateFieldsPopoverDisabled = roadmapSessionSettings?.isDateFieldsPopoverDisabled ?? true
  const setOpenDateConfigurationMenu = roadmapDateFieldsMenu?.setOpenDateConfigurationMenu
  const hasRoadmapContext = Boolean(roadmapSettings && roadmapSessionSettings)

  const uniqueDateFieldNames = useMemo(() => {
    return new Set(dateFields.filter(isRoadmapColumnModel).map(field => field.name))
  }, [dateFields])

  const uniqueDateFieldCount = uniqueDateFieldNames.size

  const initialFieldType = useMemo<InitialFieldType>(() => {
    return dateFields.some(field => isRoadmapColumnModel(field) && field.dataType === 'iteration')
      ? 'iteration'
      : 'date'
  }, [dateFields])

  const hasNoDateFields = uniqueDateFieldNames.size === 0
  const isNewRoadmapLayout =
    currentView?.serverViewState.layout !== ViewTypeParam.Roadmap &&
    currentView?.localViewState.layout === ViewTypeParam.Roadmap
  const showInitialSelectedFieldsMessage = !hasNoDateFields && areDateFieldsDefault && isNewRoadmapLayout

  const showDateFieldsPopover =
    hasRoadmapContext && !isDateFieldsPopoverDisabled && (hasNoDateFields || showInitialSelectedFieldsMessage)

  const handleDateFieldsPopoverPrimaryAction = useCallback(() => {
    if (!hasRoadmapContext) {
      return
    }

    setOpenDateConfigurationMenu?.(true)
    postStats({
      name: RoadmapDateFieldsPopoverDismissed,
      ui: RoadmapDateFieldsUIPopover,
      context: JSON.stringify(getDateFieldToolbarStatsContext(uniqueDateFieldCount, initialFieldType)),
    })
  }, [hasRoadmapContext, initialFieldType, postStats, setOpenDateConfigurationMenu, uniqueDateFieldCount])

  return {
    showDateFieldsPopover,
    showInitialSelectedFieldsMessage,
    initialFieldType,
    handleDateFieldsPopoverPrimaryAction,
  }
}
